/**
 * PATH: packages/ui/src/shortcuts/actions.ts
 * FILE_ID: UI-SHORTCUTS-ACTIONS-001
 * PURPOSE: Machine-readable action descriptors — the single source of truth
 *          the dispatcher matches against and the reference renders from.
 *          Phase 0 ports the GLOBAL ("General") shortcuts into descriptors;
 *          per-hub shortcuts migrate here in Phase 2 (expand→migrate→contract).
 * WHY: Replaces the hand-mirrored display strings in registry.ts for the
 *      general scope with real bindings + scope + ids, so the same data drives
 *      both dispatch and the read-only reference (kills drift).
 *
 * FLOW:
 *   GLOBAL_ACTIONS (here) ──┐
 *                           ├─► SHORTCUT_ACTIONS ─► dispatcher + deriveDisplay
 *   (hub manifests, Phase2)─┘
 *
 * RELATED:
 *   - ./types.ts: ShortcutAction
 *   - ./display.ts: deriveDisplaySection (reproduces the legacy 'general' tab)
 *   - ./registry.ts: legacy display registry (still drives un-migrated hubs)
 */

import type { ShortcutAction } from './types'

/**
 * Global ("General") actions — work everywhere. These mirror the legacy
 * registry's `general` section exactly (verified by display.test.ts). Entries
 * with a null defaultBinding + displayKeys are documentation-only rows (no
 * single bound key) that the reference still lists.
 */
export const GLOBAL_ACTIONS: ShortcutAction[] = [
  // allowInInput: the legacy capture-phase ⌘, handler (useLayoutState) fired
  // even while a text field was focused; keep that contract now that the
  // dispatcher (input-guarded by default) owns this chord.
  { id: 'app.settings.toggle', scope: { kind: 'global' }, defaultBinding: 'mod+comma', description: 'Open or close settings', category: 'App', allowInInput: true },
  { id: 'app.sidebar.toggle', scope: { kind: 'global' }, defaultBinding: 'mod+b', description: 'Toggle the sidebar', category: 'App' },
  { id: 'app.commandPalette.open', scope: { kind: 'global' }, defaultBinding: 'mod+k', description: 'Open the command palette', category: 'App' },
  { id: 'app.search.global', scope: { kind: 'global' }, defaultBinding: 'mod+f', description: 'Global search', category: 'App' },
  { id: 'app.help.shortcuts', scope: { kind: 'global' }, defaultBinding: '?', description: 'Show keyboard shortcuts (where available)', category: 'App' },
  { id: 'app.escape', scope: { kind: 'global' }, defaultBinding: 'escape', description: 'Close dialogs, sheets, or cancel the current action', category: 'App', editable: false, allowInInput: true },
  { id: 'nav.list.move', scope: { kind: 'global' }, defaultBinding: null, displayKeys: ['↑', '↓'], description: 'Move through command palette / list results', category: 'Navigation', editable: false },
  { id: 'nav.confirm', scope: { kind: 'global' }, defaultBinding: 'enter', description: 'Run the selected command or confirm the focused action', category: 'Navigation', editable: false },
  { id: 'nav.tabs.returnLast', scope: { kind: 'global' }, defaultBinding: null, displayKeys: ['Tab strip ↺'], description: 'Return to last opened tab (icon just left of Home) — tab positions stay put', category: 'Navigation', editable: false },
  { id: 'nav.emailHub', scope: { kind: 'global' }, defaultBinding: 'mod+e', description: 'Jump to the Email Hub — right-click any hub in the sidebar to set or change its shortcut', category: 'Navigation' },
  { id: 'app.save', scope: { kind: 'global' }, defaultBinding: 'mod+s', description: 'Save (contextual — e.g. Quote Builder)', category: 'Actions', allowInInput: true, capture: true },
  { id: 'app.commit', scope: { kind: 'global' }, defaultBinding: 'mod+enter', description: 'Commit inline edits (contextual)', category: 'Actions' },
]

/**
 * All registered action descriptors. Phase 0 = global only; hub manifests are
 * concatenated here as each hub migrates off its ad-hoc keydown handler.
 */
export const SHORTCUT_ACTIONS: ShortcutAction[] = [...GLOBAL_ACTIONS]

/** Lookup by id (undefined if unknown — overrides for unknown ids are ignored). */
export function getShortcutAction(id: string): ShortcutAction | undefined {
  return SHORTCUT_ACTIONS.find((a) => a.id === id)
}
